import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import PharmeasyLayout from "@/components/PharmeasyLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, LabelList,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from "recharts";
import { 
  Package, Truck, CheckCircle, Clock, TrendingUp, RefreshCw,
  Building2, Calendar, XCircle, Loader2, RotateCcw, User, ChevronLeft, Copy
} from "lucide-react";

const COLORS = ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

const PharmeasyDashboard = ({ user, onLogout }) => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [selectedHub, setSelectedHub] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedSprinter, setSelectedSprinter] = useState(null);
  const [sprinterDaily, setSprinterDaily] = useState(null);
  const [sprinterDailyLoading, setSprinterDailyLoading] = useState(false);
  const [dailyRiderData, setDailyRiderData] = useState(null);
  const [pinnedDailyTooltip, setPinnedDailyTooltip] = useState(null);
  const [pinnedEddTooltip, setPinnedEddTooltip] = useState(null);

  useEffect(() => {
    fetchDashboardStats();
  }, [selectedHub, dateFrom, dateTo]);

  const fetchDashboardStats = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedHub) params.append("hub", selectedHub);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      
      const res = await axios.get(`${API}/pharmeasy-orders/dashboard-stats?${params.toString()}`);
      setStats(res.data);
      setSelectedSprinter(null);
      setSprinterDaily(null);
      // Fetch daily rider deliveries
      const drParams = new URLSearchParams();
      if (selectedHub) drParams.append("hub", selectedHub);
      drParams.append("days", "14");
      axios.get(`${API}/pharmeasy-orders/daily-rider-deliveries?${drParams.toString()}`)
        .then(r => setDailyRiderData(r.data))
        .catch(() => setDailyRiderData(null));
    } catch (err) {
      toast.error("Failed to load dashboard statistics");
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num == null) return '0';
    return num.toLocaleString('en-IN');
  };

  const formatHours = (hours) => {
    if (!hours) return '0h';
    if (hours < 1) return Math.round(hours * 60) + 'm';
    if (hours < 24) return Math.round(hours) + 'h';
    return (hours / 24).toFixed(1) + 'd';
  };

  // Prepare data for charts
  const typeData = stats?.by_type ? Object.entries(stats.by_type).map(([name, value]) => ({
    name: name || 'Unknown',
    value
  })) : [];

  const hubData = stats?.by_hub?.slice(0, 10) || [];

  // Combine received and delivered by date for comparison chart
  const dateComparisonData = [];
  if (stats?.received_by_date && stats?.delivered_by_date) {
    const allDates = new Set([
      ...stats.received_by_date.map(d => d.date),
      ...stats.delivered_by_date.map(d => d.date)
    ]);
    Array.from(allDates).sort().forEach(date => {
      const received = stats.received_by_date.find(d => d.date === date)?.count || 0;
      const deliveredEntry = stats.delivered_by_date.find(d => d.date === date);
      const delivered = deliveredEntry?.count || 0;
      dateComparisonData.push({
        date: date.slice(5), // MM-DD format
        fullDate: date,
        received,
        delivered,
        edd_exact: deliveredEntry?.edd_exact || 0,
        future_edd: deliveredEntry?.future_edd || {},
        no_edd: deliveredEntry?.no_edd || 0,
        breach: deliveredEntry?.breach || {},
      });
    });
  }

  // EDD Status data for the new chart
  const eddStatusData = stats?.edd_status_data?.data || [];
  const eddStatuses = stats?.edd_status_data?.statuses || [];
  const STATUS_COLORS = {
    DELIVERED: "#10b981", OUT_FOR_DELIVERY: "#f59e0b", CANCELLED: "#94a3b8",
    IN_TRANSIT: "#6366f1", REACH_DESTINATION: "#06b6d4", DISPATCHED: "#8b5cf6",
    FORWARD_DISPATCHED: "#8b5cf6", RETURN_DISPATCHED: "#f43f5e", RETURNED: "#ef4444",
    UNDELIVERED: "#dc2626", RTO_IN_TRANSIT: "#fb923c", RTO_DELIVERED: "#a855f7",
    PICKUP_PENDING: "#d97706", PICKED_UP: "#2563eb", BOOKED: "#3b82f6",
  };
  const getStatusColor = (status) => STATUS_COLORS[status] || `hsl(${(status || "").split("").reduce((a, c) => a + c.charCodeAt(0), 0) % 360}, 55%, 55%)`;
  const eddChartData = eddStatusData.map((d) => ({ ...d, dateLabel: d.date?.slice(5) || d.date }));

  // Helper: copy text to clipboard with toast
  const copyText = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success("Copied to clipboard")).catch(() => {});
  };

  // No hover tooltips — click only
  const NoTooltip = () => null;

  const avgDeliveryData = stats?.avg_delivery_by_date?.map(d => ({
    date: d.date.slice(5),
    avgHours: d.avg_hours,
    count: d.count
  })) || [];

  // Sprinter delivery data
  const sprinterData = stats?.sprinter_deliveries || [];
  const totalSprinterDeliveries = sprinterData.reduce((s, d) => s + d.count, 0);

  const handleSprinterClick = async (data) => {
    if (!data?.name) return;
    const name = data.name;
    if (selectedSprinter === name) {
      setSelectedSprinter(null);
      setSprinterDaily(null);
      return;
    }
    setSelectedSprinter(name);
    setSprinterDailyLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedHub) params.append("hub", selectedHub);
      const res = await axios.get(`${API}/pharmeasy-orders/sprinter-daily/${encodeURIComponent(name)}?${params}`);
      setSprinterDaily(res.data.daily || []);
    } catch {
      toast.error("Failed to load sprinter daily data");
      setSprinterDaily(null);
    } finally {
      setSprinterDailyLoading(false);
    }
  };

  const sprinterDailyChartData = sprinterDaily?.map(d => ({
    date: d.date.slice(5),
    deliveries: d.count
  })) || [];

  // Universal Loader
  if (loading) {
    return (
      <PharmeasyLayout user={user} onLogout={onLogout}>
        <div className="flex flex-col items-center justify-center h-[60vh]">
          <Loader2 className="w-12 h-12 animate-spin text-emerald-500" />
          <p className="text-slate-500 mt-4 text-lg">Loading Dashboard...</p>
        </div>
      </PharmeasyLayout>
    );
  }

  return (
    <PharmeasyLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="pharmeasy-dashboard">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Pharmeasy Dashboard</h1>
            <p className="text-slate-500 mt-1">Analytics and insights for operations</p>
          </div>
          
          {/* Filters */}
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-600">Hub:</Label>
              <Select value={selectedHub || "all"} onValueChange={(val) => setSelectedHub(val === "all" ? "" : val)}>
                <SelectTrigger className="w-48" data-testid="hub-filter">
                  <SelectValue placeholder="All Hubs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Hubs</SelectItem>
                  {stats?.all_hubs?.map(hub => (
                    <SelectItem key={hub} value={hub}>{hub}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-600">From:</Label>
              <input 
                type="date" 
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
                data-testid="date-from"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Label className="text-sm text-slate-600">To:</Label>
              <input 
                type="date" 
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-3 py-2 border rounded-md text-sm"
                data-testid="date-to"
              />
            </div>
            
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchDashboardStats}
              disabled={loading}
              data-testid="refresh-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-slate-50 to-slate-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-600">Total Orders</p>
                  <p className="text-3xl font-bold text-slate-800">{formatNumber(stats?.summary?.total_all_orders)}</p>
                </div>
                <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center">
                  <Package className="w-6 h-6 text-slate-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-orange-50 to-orange-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-600">Out for Delivery</p>
                  <p className="text-3xl font-bold text-orange-800">{formatNumber(stats?.summary?.ofd)}</p>
                </div>
                <div className="w-12 h-12 bg-orange-200 rounded-full flex items-center justify-center">
                  <Truck className="w-6 h-6 text-orange-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-blue-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-600">Delivered</p>
                  <p className="text-3xl font-bold text-blue-800">{formatNumber(stats?.summary?.delivered)}</p>
                  <p className="text-xs text-blue-600 mt-1">
                    {stats?.summary?.delivery_rate}% delivery rate
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-200 rounded-full flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-blue-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-amber-50 to-amber-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-amber-600">Unassigned</p>
                  <p className="text-3xl font-bold text-amber-800">{formatNumber(stats?.summary?.unassigned)}</p>
                  <p className="text-xs text-amber-600 mt-1">
                    Matching Orders page
                  </p>
                </div>
                <div className="w-12 h-12 bg-amber-200 rounded-full flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-red-50 to-red-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-600">Cancelled</p>
                  <p className="text-3xl font-bold text-red-800">{formatNumber(stats?.summary?.cancelled)}</p>
                </div>
                <div className="w-12 h-12 bg-red-200 rounded-full flex items-center justify-center">
                  <XCircle className="w-6 h-6 text-red-700" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-purple-100">
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600">Avg Delivery Time</p>
                  <p className="text-3xl font-bold text-purple-800">
                    {formatHours(stats?.delivery_time_stats?.avg_hours)}
                  </p>
                  <p className="text-xs text-purple-600 mt-1">
                    Min: {formatHours(stats?.delivery_time_stats?.min_hours)} | Max: {formatHours(stats?.delivery_time_stats?.max_hours)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-200 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-purple-700" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Return Orders Summary Card */}
        {stats?.return_orders_summary?.total > 0 && (
          <Card className="border-0 shadow-lg bg-gradient-to-br from-teal-50 to-cyan-100" data-testid="return-orders-summary">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-lg text-teal-800">
                <RotateCcw className="w-5 h-5 text-teal-600" />
                Return Orders Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white/60 rounded-lg p-4">
                  <p className="text-sm font-medium text-teal-600">Total Returns</p>
                  <p className="text-2xl font-bold text-teal-800">{formatNumber(stats?.return_orders_summary?.total)}</p>
                </div>
                <div className="bg-white/60 rounded-lg p-4">
                  <p className="text-sm font-medium text-emerald-600">Dispatched</p>
                  <p className="text-2xl font-bold text-emerald-700">{formatNumber(stats?.return_orders_summary?.dispatched)}</p>
                  <p className="text-xs text-emerald-600 mt-1">
                    {stats?.return_orders_summary?.dispatch_rate}% dispatch rate
                  </p>
                </div>
                <div className="bg-white/60 rounded-lg p-4">
                  <p className="text-sm font-medium text-amber-600">Pending Dispatch</p>
                  <p className="text-2xl font-bold text-amber-700">{formatNumber(stats?.return_orders_summary?.pending)}</p>
                </div>
                <div className="bg-white/60 rounded-lg p-4">
                  <p className="text-sm font-medium text-slate-600">Status Breakdown</p>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {stats?.return_orders_summary?.by_status?.slice(0, 4).map((s, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs bg-white">
                        {s.status}: {s.count}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Charts Row 1 */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Shipment Type Distribution (Pie Chart) */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Package className="w-5 h-5 text-emerald-600" />
                Shipment Type Distribution
              </CardTitle>
            </CardHeader>
            <CardContent>
              {typeData.length > 0 ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={typeData}
                      cx="50%"
                      cy="45%"
                      labelLine={true}
                      label={({ name, percent, value }) => `${name}: ${formatNumber(value)} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={75}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {typeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(value) => formatNumber(value)} />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex items-center justify-center text-slate-400">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* OFD Count vs Shipments */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Truck className="w-5 h-5 text-blue-600" />
                OFD Count vs Shipments
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(stats?.ofd_distribution?.length > 0) ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={stats.ofd_distribution} margin={{ top: 15, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="ofd_count" label={{ value: "No. of OFD", position: "insideBottom", offset: -2, fontSize: 11 }} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => [formatNumber(value), "Shipments"]} labelFormatter={(l) => `OFD Count: ${l}`} />
                    <Bar dataKey="shipments" fill="#3b82f6" radius={[4, 4, 0, 0]}>
                      <LabelList dataKey="shipments" position="top" fontSize={10} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex items-center justify-center text-slate-400">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Unattempted Count vs Shipments */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <XCircle className="w-5 h-5 text-red-600" />
                Unattempted / Undelivered vs Shipments
              </CardTitle>
            </CardHeader>
            <CardContent>
              {(stats?.unattempted_distribution?.length > 0) ? (
                <ResponsiveContainer width="100%" height={280}>
                  <BarChart data={stats.unattempted_distribution} margin={{ top: 15, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="unattempted_count" label={{ value: "No. of Unattempted", position: "insideBottom", offset: -2, fontSize: 11 }} tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(value) => [formatNumber(value), "Shipments"]} labelFormatter={(l) => `Unattempted Count: ${l}`} />
                    <Bar dataKey="shipments" fill="#ef4444" radius={[4, 4, 0, 0]}>
                      <LabelList dataKey="shipments" position="top" fontSize={10} />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[280px] flex items-center justify-center text-slate-400">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 2 */}
        <div className="grid grid-cols-1 gap-6">
          {/* Daily Received vs Delivered Comparison */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="w-5 h-5 text-indigo-600" />
                Daily Received vs Delivered (Last 14 Days)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dateComparisonData.length > 0 ? (
                <div className="relative">
                <ResponsiveContainer width="100%" height={350}>
                <BarChart data={dateComparisonData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis />
                  <Tooltip content={<NoTooltip />} />
                  <Legend />
                  <Bar dataKey="received" fill="#f59e0b" name="Received" cursor="pointer" onClick={(data) => { if (data) setPinnedDailyTooltip(data); }} />
                  <Bar dataKey="delivered" fill="#10b981" name="Delivered" cursor="pointer" onClick={(data) => { if (data) setPinnedDailyTooltip(data); }} />
                </BarChart>
                </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[350px] flex items-center justify-center text-slate-400">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Order Status Count with EDD */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Calendar className="w-5 h-5 text-purple-600" />
                Order Status Count by EDD Date
              </CardTitle>
              <p className="text-xs text-slate-500 mt-1">Historical, current, and future EDD dates with status breakdown</p>
            </CardHeader>
            <CardContent>
              {eddChartData.length > 0 ? (
                <div className="relative">
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={eddChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="dateLabel"
                      tick={({ x, y, payload }) => {
                        const entry = eddChartData.find((d) => d.dateLabel === payload.value);
                        const isToday = entry?.label === "today";
                        const isFuture = entry?.label === "future";
                        return (
                          <text x={x} y={y + 12} textAnchor="middle" fontSize={10}
                            fill={isToday ? "#00A699" : isFuture ? "#6366f1" : "#64748b"}
                            fontWeight={isToday ? 700 : 400}>
                            {payload.value}
                          </text>
                        );
                      }}
                    />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip content={<NoTooltip />} />
                    <Legend
                      formatter={(value) => <span className="text-xs">{value.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}</span>}
                    />
                    {eddStatuses.map((st) => (
                      <Bar key={st} dataKey={st} stackId="a" fill={getStatusColor(st)} name={st} cursor="pointer"
                        onClick={(data) => { if (data) setPinnedEddTooltip(data); }}
                      />
                    ))}
                  </BarChart>
                </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[400px] flex items-center justify-center text-slate-400">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Charts Row 3 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Shipments by Hub */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="w-5 h-5 text-rose-600" />
                Shipments by Hub
              </CardTitle>
            </CardHeader>
            <CardContent>
              {hubData.length > 0 ? (
                <BarChart width={500} height={350} data={hubData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="hub" tick={{ fontSize: 9, angle: -45, textAnchor: 'end' }} height={80} />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="delivered" stackId="a" fill="#10b981" name="Delivered" />
                  <Bar dataKey="pending" stackId="a" fill="#f59e0b" name="Pending" />
                  <Bar dataKey="cancelled" stackId="a" fill="#ef4444" name="Cancelled" />
                </BarChart>
              ) : (
                <div className="h-[350px] flex items-center justify-center text-slate-400">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              )}
            </CardContent>
          </Card>

          {/* Average Delivery Time Trend */}
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <TrendingUp className="w-5 h-5 text-cyan-600" />
                Average Delivery Time Trend (Hours)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {avgDeliveryData.length > 0 ? (
                <ResponsiveContainer width="100%" height={350}>
                <LineChart data={avgDeliveryData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                  <YAxis label={{ value: "Avg Hours", angle: -90, position: "insideLeft", fontSize: 11 }} />
                  <Tooltip 
                    formatter={(value, name) => [
                      name === 'avgHours' ? `${value}h` : value,
                      name === 'avgHours' ? 'Avg Time' : 'Deliveries'
                    ]}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="avgHours" 
                    stroke="#06b6d4" 
                    strokeWidth={2}
                    dot={{ fill: '#06b6d4', r: 4 }}
                    name="Avg Hours"
                  />
                </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[350px] flex items-center justify-center text-slate-400">
                  <Loader2 className="w-8 h-8 animate-spin" />
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Daily Rider Deliveries — Date-wise breakdown */}
        {dailyRiderData?.dates?.length > 0 && (() => {
          const dates = dailyRiderData.dates;
          // Get top riders by total across all dates
          const riderTotals = {};
          dates.forEach(d => d.riders.forEach(r => {
            riderTotals[r.name] = (riderTotals[r.name] || 0) + r.count;
          }));
          const topRiders = Object.entries(riderTotals)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 10)
            .map(([name]) => name);

          const RIDER_COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16', '#f97316', '#14b8a6'];
          const chartData = dates.map(d => {
            const row = { date: d.date.slice(5), total: d.total, riderCount: d.riders.length };
            topRiders.forEach(name => { row[name] = 0; });
            let othersCount = 0;
            d.riders.forEach(r => {
              if (topRiders.includes(r.name)) row[r.name] = r.count;
              else othersCount += r.count;
            });
            row["Others"] = othersCount;
            return row;
          });

          return (
            <Card className="border-0 shadow-lg" data-testid="daily-rider-deliveries-card">
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-lg">
                    <Calendar className="w-5 h-5 text-teal-600" />
                    Date-wise Rider Deliveries
                  </div>
                  <span className="text-sm font-normal text-slate-500">
                    Last {dates.length} days
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={400}>
                  <BarChart data={chartData} margin={{ top: 30, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                    <Tooltip
                      content={({ active, payload, label }) => {
                        if (!active || !payload?.length) return null;
                        const items = payload.filter(p => p.value > 0 && p.dataKey !== "Others").sort((a, b) => b.value - a.value);
                        const othersItem = payload.find(p => p.dataKey === "Others" && p.value > 0);
                        const total = payload.reduce((s, p) => s + (p.value || 0), 0);
                        const riderCount = payload[0]?.payload?.riderCount || 0;
                        return (
                          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "8px 12px", fontSize: 12 }}>
                            <div style={{ fontWeight: 700, marginBottom: 4, borderBottom: "1px solid #e2e8f0", paddingBottom: 4 }}>
                              {label} — <span style={{ color: "#10b981" }}>Total: {total}</span> <span style={{ color: "#64748b" }}>({riderCount} riders)</span>
                            </div>
                            {items.map(p => (
                              <div key={p.dataKey} style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "1px 0" }}>
                                <span style={{ color: p.fill }}>{p.dataKey}</span>
                                <span style={{ fontWeight: 600 }}>{p.value}</span>
                              </div>
                            ))}
                            {othersItem && (
                              <div style={{ display: "flex", justifyContent: "space-between", gap: 16, padding: "1px 0" }}>
                                <span style={{ color: "#cbd5e1" }}>Others</span>
                                <span style={{ fontWeight: 600 }}>{othersItem.value}</span>
                              </div>
                            )}
                          </div>
                        );
                      }}
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {topRiders.map((name, i) => (
                      <Bar key={name} dataKey={name} stackId="riders" fill={RIDER_COLORS[i % RIDER_COLORS.length]}>
                        <LabelList dataKey={name} position="center" fill="#fff" fontSize={9} fontWeight={700}
                          formatter={(v) => v >= 5 ? v : ""} />
                      </Bar>
                    ))}
                    <Bar dataKey="Others" stackId="riders" fill="#cbd5e1">
                      <LabelList dataKey="Others" position="center" fill="#64748b" fontSize={9} fontWeight={700}
                        formatter={(v) => v >= 5 ? v : ""} />
                      <LabelList
                        content={({ x, y, width, index }) => {
                          const d = chartData[index];
                          if (!d) return null;
                          return (
                            <text x={x + width / 2} y={y - 6} textAnchor="middle" fill="#334155" fontSize={11} fontWeight={800}>{d.total}</text>
                          );
                        }}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          );
        })()}

        {/* Sprinter Delivery Performance */}
        {sprinterData.length > 0 && (
          <Card className="border-0 shadow-lg" data-testid="sprinter-deliveries-card">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-lg">
                  <User className="w-5 h-5 text-indigo-600" />
                  {selectedSprinter ? (
                    <>
                      <button onClick={() => { setSelectedSprinter(null); setSprinterDaily(null); }}
                        className="text-indigo-600 hover:text-indigo-800 transition-colors">
                        <ChevronLeft className="w-5 h-5 inline" />
                      </button>
                      <span>{selectedSprinter} — Daily Deliveries</span>
                    </>
                  ) : (
                    "Delivery by Rider"
                  )}
                </div>
                {!selectedSprinter && (
                  <span className="text-sm font-normal text-slate-500">{sprinterData.length} riders, {totalSprinterDeliveries} total deliveries</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {!selectedSprinter ? (
                /* Horizontal Bar Chart — Total per Sprinter */
                <div style={{ height: Math.max(300, sprinterData.length * 36) }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sprinterData} layout="vertical" margin={{ top: 5, right: 30, left: 10, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="name" type="category" width={140} tick={{ fontSize: 11 }} />
                      <Tooltip
                        formatter={(value) => [value, "Deliveries"]}
                        contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                      />
                      <Bar
                        dataKey="count"
                        radius={[0, 4, 4, 0]}
                        cursor="pointer"
                        onClick={(data) => handleSprinterClick(data)}
                      >
                        {sprinterData.map((entry, index) => (
                          <Cell
                            key={`cell-${index}`}
                            fill={index === 0 ? "#4f46e5" : index === 1 ? "#6366f1" : index === 2 ? "#818cf8" : "#a5b4fc"}
                          />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              ) : sprinterDailyLoading ? (
                <div className="h-[300px] flex items-center justify-center">
                  <Loader2 className="w-8 h-8 animate-spin text-indigo-500" />
                </div>
              ) : sprinterDailyChartData.length > 0 ? (
                /* Date-wise Bar Chart for selected sprinter */
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={sprinterDailyChartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} />
                    <Tooltip
                      formatter={(value) => [value, "Deliveries"]}
                      contentStyle={{ borderRadius: "8px", border: "1px solid #e2e8f0" }}
                    />
                    <Bar dataKey="deliveries" fill="#6366f1" radius={[4, 4, 0, 0]} name="Deliveries" />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <div className="h-[300px] flex items-center justify-center text-slate-400">No date-wise data available</div>
              )}

              {/* Mini Leaderboard Table */}
              {!selectedSprinter && sprinterData.length > 0 && (
                <div className="mt-4 border-t pt-4">
                  <table className="w-full text-sm">
                    <thead className="bg-slate-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">#</th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Rider</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Deliveries</th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-700 whitespace-nowrap">Share</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {sprinterData.map((s, i) => (
                        <tr key={s.name} className="hover:bg-indigo-50 cursor-pointer transition-colors"
                          onClick={() => handleSprinterClick(s)} data-testid={`sprinter-row-${i}`}>
                          <td className="px-4 py-3 align-middle font-medium text-slate-500">
                            {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                          </td>
                          <td className="px-4 py-3 align-middle font-medium text-slate-800">{s.name}</td>
                          <td className="px-4 py-3 align-middle text-right font-medium text-indigo-600">{s.count}</td>
                          <td className="px-4 py-3 align-middle text-right text-slate-500">
                            {totalSprinterDeliveries > 0 ? Math.round(s.count / totalSprinterDeliveries * 100) : 0}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Type by Hub Table */}
        {stats?.by_type_and_hub?.length > 0 && (
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Package className="w-5 h-5 text-violet-600" />
                Shipments by Type & Hub
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Hub</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Type</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Total</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Delivered</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Rate</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {stats.by_type_and_hub.slice(0, 20).map((row, idx) => (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="px-4 py-3 align-middle text-sm text-slate-800">{row.hub}</td>
                        <td className="px-4 py-3 align-middle">
                          <Badge className={
                            row.type === 'FORWARD' ? 'bg-blue-100 text-blue-700' :
                            row.type === 'DELIVERY' ? 'bg-emerald-100 text-emerald-700' :
                            row.type === 'RETURN' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-700'
                          }>
                            {row.type}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 align-middle text-sm text-right font-medium text-slate-800">{row.total}</td>
                        <td className="px-4 py-3 align-middle text-sm text-right text-emerald-600 font-medium">{row.delivered}</td>
                        <td className="px-4 py-3 align-middle text-sm text-right">
                          <span className={`font-medium ${
                            (row.delivered / row.total * 100) >= 90 ? 'text-emerald-600' :
                            (row.delivered / row.total * 100) >= 70 ? 'text-amber-600' :
                            'text-red-600'
                          }`}>
                            {row.total > 0 ? Math.round(row.delivered / row.total * 100) : 0}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Click popup for Daily Received vs Delivered */}
      {pinnedDailyTooltip && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={() => setPinnedDailyTooltip(null)}>
          <div className="bg-white border border-slate-200 rounded-xl shadow-2xl p-4 text-xs min-w-[260px] max-w-[340px]" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const data = pinnedDailyTooltip;
              const { received, delivered, edd_exact, future_edd, no_edd, breach } = data;
              const futureEntries = Object.entries(future_edd || {}).sort(([a], [b]) => Number(a) - Number(b));
              const breachEntries = Object.entries(breach || {}).sort(([a], [b]) => Number(a) - Number(b));
              const hasEddData = delivered > 0 && (edd_exact > 0 || futureEntries.length > 0 || breachEntries.length > 0);
              const totalWithEdd = delivered - (no_edd || 0);
              const pct = (val) => totalWithEdd > 0 ? ((val / totalWithEdd) * 100).toFixed(1) : "0.0";
              const getText = () => {
                let t = `${data.fullDate}\nReceived: ${received}\nDelivered: ${delivered}\n`;
                if (hasEddData) {
                  t += "\nEDD Breakdown:\n";
                  if (edd_exact > 0) t += `EDD: ${edd_exact} (${pct(edd_exact)}%)\n`;
                  futureEntries.forEach(([d, c]) => { t += `EDD +${d} (Future): ${c} (${pct(c)}%)\n`; });
                  breachEntries.forEach(([d, c]) => { t += `EDD -${d}: ${c} (${pct(c)}%)\n`; });
                  if (no_edd > 0) t += `No EDD: ${no_edd}\n`;
                }
                return t;
              };
              return (<>
                <div className="flex items-center gap-2 mb-2 pb-2 border-b">
                  <span className="font-semibold text-sm text-slate-700 flex-1">{data.fullDate}</span>
                  <button onClick={() => copyText(getText())} className="p-1.5 hover:bg-slate-100 rounded" title="Copy"><Copy className="w-4 h-4 text-slate-500" /></button>
                  <button onClick={() => setPinnedDailyTooltip(null)} className="p-1.5 hover:bg-red-50 rounded" title="Close"><XCircle className="w-4 h-4 text-slate-400 hover:text-red-500" /></button>
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2.5 h-2.5 rounded-sm bg-amber-400 flex-shrink-0" />
                  <span className="text-slate-600">Received:</span>
                  <span className="font-bold text-slate-800 ml-auto">{received}</span>
                </div>
                <div className="flex items-center gap-2 mb-1">
                  <span className="w-2.5 h-2.5 rounded-sm bg-emerald-500 flex-shrink-0" />
                  <span className="text-slate-600">Delivered:</span>
                  <span className="font-bold text-slate-800 ml-auto">{delivered}</span>
                </div>
                {hasEddData && (
                  <div className="mt-2 pt-2 border-t border-slate-100">
                    <p className="font-semibold text-slate-500 mb-1.5 uppercase tracking-wide" style={{ fontSize: '10px' }}>EDD Breakdown</p>
                    {edd_exact > 0 && (<div className="flex items-center justify-between py-0.5"><span className="text-emerald-700 font-medium">EDD</span><span className="font-bold text-emerald-700">{edd_exact} <span className="font-normal text-slate-400">({pct(edd_exact)}%)</span></span></div>)}
                    {futureEntries.map(([days, count]) => (<div key={`f${days}`} className="flex items-center justify-between py-0.5"><span className="text-cyan-600">EDD +{days} (Future)</span><span className="font-bold text-cyan-600">{count} <span className="font-normal text-slate-400">({pct(count)}%)</span></span></div>))}
                    {breachEntries.map(([days, count]) => (<div key={`b${days}`} className="flex items-center justify-between py-0.5"><span className="text-red-600">EDD -{days}</span><span className="font-bold text-red-600">{count} <span className="font-normal text-slate-400">({pct(count)}%)</span></span></div>))}
                    {no_edd > 0 && (<div className="flex items-center justify-between py-0.5"><span className="text-slate-400">No EDD</span><span className="font-bold text-slate-400">{no_edd}</span></div>)}
                  </div>
                )}
              </>);
            })()}
          </div>
        </div>
      )}

      {/* Click popup for EDD Status chart */}
      {pinnedEddTooltip && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={() => setPinnedEddTooltip(null)}>
          <div className="bg-white border border-slate-200 rounded-xl shadow-2xl p-4 text-xs min-w-[280px] max-w-[380px]" onClick={(e) => e.stopPropagation()}>
            {(() => {
              const data = pinnedEddTooltip;
              const tagColor = data.label === "today" ? "#00A699" : data.label === "future" ? "#6366f1" : "#94a3b8";
              const tagLabel = data.label === "today" ? "Today" : data.label === "future" ? "Future" : "Past";
              const cancelledCount = data["CANCELLED"] || 0;
              const totalAll = eddStatuses.reduce((sum, st) => sum + (data[st] || 0), 0);
              const totalExclCancelled = totalAll - cancelledCount;
              const pct = (val) => totalExclCancelled > 0 ? ((val / totalExclCancelled) * 100).toFixed(1) : "0.0";
              const eddDel = data.edd_delivery;
              const beforeWithin = eddDel?.before_within_edd || 0;
              const afterEdd = eddDel?.after_edd || 0;
              const beforeByDate = Object.entries(eddDel?.before_by_date || {}).sort(([a], [b]) => a.localeCompare(b));
              const afterByDate = Object.entries(eddDel?.after_by_date || {}).sort(([a], [b]) => a.localeCompare(b));
              const eddPct = (val) => totalExclCancelled > 0 ? ((val / totalExclCancelled) * 100).toFixed(1) : "0.0";
              const getText = () => {
                let t = `${data.date} (${tagLabel}) | Total: ${totalAll} (excl cancelled: ${totalExclCancelled})\n`;
                eddStatuses.forEach((st) => { if (data[st]) { if (st === "CANCELLED") t += `${st}: ${data[st]}\n`; else t += `${st}: ${data[st]} (${pct(data[st])}%)\n`; } });
                if (beforeWithin + afterEdd > 0) {
                  t += `\nEDD Delivery:\nBefore & Within EDD: ${beforeWithin} (${eddPct(beforeWithin)}%)\n`;
                  beforeByDate.forEach(([d, c]) => { t += `  ${d}: ${c} (${eddPct(c)}%)\n`; });
                  t += `After EDD: ${afterEdd} (${eddPct(afterEdd)}%)\n`;
                  afterByDate.forEach(([d, c]) => { t += `  ${d}: ${c} (${eddPct(c)}%)\n`; });
                }
                return t;
              };
              return (<>
                <div className="flex items-center gap-2 mb-2 pb-2 border-b">
                  <span className="font-semibold text-sm text-slate-700">{data.date}</span>
                  <span className="text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ background: `${tagColor}15`, color: tagColor }}>{tagLabel}</span>
                  <div className="ml-auto flex items-center gap-0.5">
                    <button onClick={() => copyText(getText())} className="p-1.5 hover:bg-slate-100 rounded" title="Copy"><Copy className="w-4 h-4 text-slate-500" /></button>
                    <button onClick={() => setPinnedEddTooltip(null)} className="p-1.5 hover:bg-red-50 rounded" title="Close"><XCircle className="w-4 h-4 text-slate-400 hover:text-red-500" /></button>
                  </div>
                </div>
                <div className="flex items-center justify-between py-0.5 mb-1">
                  <span className="text-slate-500 font-semibold">Total</span>
                  <span className="font-bold text-slate-800">{totalAll}</span>
                </div>
                {eddStatuses.filter(st => st !== "CANCELLED").map((st) => {
                  const val = data[st];
                  if (!val) return null;
                  return (<div key={st} className="flex items-center justify-between py-0.5"><div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: getStatusColor(st) }} /><span className="text-slate-600">{st.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, c => c.toUpperCase())}</span></div><span className="font-bold text-slate-800 ml-3">{val} <span className="font-normal text-slate-400">({pct(val)}%)</span></span></div>);
                })}
                {cancelledCount > 0 && (
                  <div className="flex items-center justify-between py-0.5 text-slate-400"><div className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: getStatusColor("CANCELLED") }} /><span>Cancelled</span></div><span className="font-bold">{cancelledCount}</span></div>
                )}
                {(beforeWithin + afterEdd > 0) && (
                  <div className="mt-2 pt-2 border-t border-slate-100">
                    <p className="font-semibold text-slate-500 mb-1 uppercase tracking-wide" style={{ fontSize: '10px' }}>EDD Delivery Performance</p>
                    <div className="flex items-center justify-between py-0.5"><span className="text-emerald-700 font-semibold">Before & Within EDD</span><span className="font-bold text-emerald-700">{beforeWithin} <span className="font-normal text-slate-400">({eddPct(beforeWithin)}%)</span></span></div>
                    {beforeByDate.map(([date, count]) => (
                      <div key={`bw${date}`} className="flex items-center justify-between py-0.5 pl-3">
                        <span className="text-emerald-600 text-[11px]">{date}</span>
                        <span className="font-medium text-emerald-600">{count} <span className="font-normal text-slate-400">({eddPct(count)}%)</span></span>
                      </div>
                    ))}
                    <div className="flex items-center justify-between py-0.5 mt-1"><span className="text-red-600 font-semibold">After EDD</span><span className="font-bold text-red-600">{afterEdd} <span className="font-normal text-slate-400">({eddPct(afterEdd)}%)</span></span></div>
                    {afterByDate.map(([date, count]) => (
                      <div key={`af${date}`} className="flex items-center justify-between py-0.5 pl-3">
                        <span className="text-red-500 text-[11px]">{date}</span>
                        <span className="font-medium text-red-500">{count} <span className="font-normal text-slate-400">({eddPct(count)}%)</span></span>
                      </div>
                    ))}
                  </div>
                )}
              </>);
            })()}
          </div>
        </div>
      )}

    </PharmeasyLayout>
  );
};

export default PharmeasyDashboard;
